import React, { useState, useEffect, useRef } from 'react';
import { useEmployeePhoto } from "../../utils/employeePhoto"; // Owner (2026-10-01): database photo
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { DashboardPageHeader } from '../../Components/Common/DashboardKit';
import api from '../../api';
import {
    Alert, Card, CardBody, Modal, ModalHeader, ModalBody,
    ModalFooter, Button, Table, Spinner
} from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import DigitalClock from '../../Components/DigitalClock';
import WhoisInBuilding from '../../pages/Jobs/CandidateList/GridView/index';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiLogIn, FiLogOut, FiCoffee, FiUserCheck,
    FiBriefcase, FiActivity, FiUserX, FiClock,
} from "react-icons/fi";
import { getPunchLocation } from "../../utils/terminalMap";
import { inkFor } from "../../utils/themeColor";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   Live elapsed-time timer
───────────────────────────────────────────── */
const Timer = ({ clockInTime }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (!clockInTime) return;
        const start = new Date(clockInTime);
        const id = setInterval(() => setElapsed(Date.now() - start), 1000);
        return () => clearInterval(id);
    }, [clockInTime]);

    const pad = (n) => String(Math.floor(n)).padStart(2, '0');
    const s = elapsed / 1000;
    const h = s / 3600;
    const m = (s % 3600) / 60;
    const sec = s % 60;
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

/* ─────────────────────────────
   Reusable confirm modal
   Round TA-1b fix: this was defined INSIDE the page component, so the 1-second
   live-clock tick gave it a new component identity every render — React
   unmounted and remounted the open modal each second, replaying the "flip"
   animation over and over ("card keeps appearing"). Hoisted to module scope so
   its identity is stable and the modal mounts exactly once per open.
───────────────────────────── */
// Go-live parity (2026-09-24), ATT#19/ATT#28: the plant (Eastern) date and hour.
const easternParts = () => {
    try {
        const p = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", hourCycle: "h23",
        }).formatToParts(new Date());
        const get = (t) => (p.find((x) => x.type === t) || {}).value;
        return { date: `${get("year")}-${get("month")}-${get("day")}`, hour: Number(get("hour")) };
    } catch {
        const d = new Date();
        return { date: d.toISOString().split("T")[0], hour: d.getHours() };
    }
};

// Expected clock-out = the end of the working day by company policy, NOT the
// clock-in time plus a number of hours: an employee who clocks in at 7:56 is
// still expected until 4:30 PM. The time comes from the server (my-status);
// 4:30 PM is used only when the server did not send one.
const expectedClockOutText = (clockInTime, expectedClockOut) => {
    if (!clockInTime) return '—';
    let end = expectedClockOut ? new Date(expectedClockOut) : null;
    if (!end || Number.isNaN(end.getTime())) {
        end = new Date(clockInTime);
        end.setHours(16, 30, 0, 0);
    }
    return end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ConfirmModal = ({ isOpen, toggle, title, onConfirm, confirmLabel, confirmColor = "primary", children }) => (
    <Modal isOpen={isOpen} toggle={toggle} modalClassName="flip" centered>
        <ModalHeader toggle={toggle}>
            <span style={{ fontWeight: 600 }}>{title}</span>
        </ModalHeader>
        <ModalBody className="text-center p-5">
            <div className="mt-2">
                <h5 className="mb-1">{title}</h5>
                <p className="text-muted mb-0">Please confirm your action.</p>
                <div className="mt-3">
                    <DigitalClock />
                </div>
                {children}
            </div>
        </ModalBody>
        <ModalFooter>
            <Button color={confirmColor} onClick={onConfirm}>{confirmLabel}</Button>
            <Button color="secondary" outline onClick={toggle}>Cancel</Button>
        </ModalFooter>
    </Modal>
);


/* ─────────────────────────────────────────────
   Activity feed item shape
───────────────────────────────────────────── */
const FEED_MAX = 30;

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const DashboardCrypto = () => {
    document.title = "SSW Technologies – Time & Attendance";

    const navigate = useNavigate();

    /* ── modal visibility ── */
    const [mFirst, setMFirst] = useState(false);
    const [mSecond, setMSecond] = useState(false);
    const [mClockIn, setMClockIn] = useState(false);
    const [clockInType, setClockInType] = useState("tod");       // Go-live parity (2026-09-24), ATT#28
    const [missedOutNote, setMissedOutNote] = useState(null);    // Go-live parity (2026-09-24), ATT#19
    const [mClockOut, setMClockOut] = useState(false);
    const [mLunchOut, setMLunchOut] = useState(false);
    const [mLunchIn, setMLunchIn] = useState(false);
    const [mBizOut, setMBizOut] = useState(false);
    const [mBizIn, setMBizIn] = useState(false);
    const [mPerOut, setMPerOut] = useState(false);
    const [mPerIn, setMPerIn] = useState(false);
    const [mMissing, setMMissing] = useState(false);
    // Round TA-2 — real missing-punch data (legacy ClockYourMissingPunches
    // parity, 21-day self view). null = loading, undefined = load failed.
    const [missingData, setMissingData] = useState(null);

    // Round TA-3 — employee correction requests (legacy
    // ClockYourMissingPunches submit parity). Server validates everything
    // again (past-day only, 21-day window, duplicates, ownership).
    const [reqForm, setReqForm] = useState({ open: false, date: "", time: "", type: "Normal", inout: "IN" });
    const [reqBusy, setReqBusy] = useState(false);
    const [reqMsg, setReqMsg] = useState(null);

    const submitMissingRequest = async () => {
        if (!reqForm.date || !reqForm.time) {
            setReqMsg({ ok: false, text: "Pick the day and the time." });
            return;
        }
        setReqBusy(true);
        setReqMsg(null);
        try {
            const res = await api.post("WhosInBuilding/missing-punch-request", {
                reportDate: reqForm.date,
                clockTime: `${reqForm.date}T${reqForm.time}:00`,
                clockType: reqForm.type,
                status: reqForm.inout,
            });
            setReqMsg({ ok: true, text: res?.data?.message || "Request submitted." });
            setReqForm((f) => ({ ...f, open: false }));
            await openMissingReload();
        } catch (e) {
            const d = e?.response?.data;
            setReqMsg({ ok: false, text: (typeof d === "string" && d) || d?.message || "Request failed." });
        } finally {
            setReqBusy(false);
        }
    };

    const requestDeletion = async (punchId) => {
        setReqBusy(true);
        setReqMsg(null);
        try {
            const res = await api.post("WhosInBuilding/punch-deletion-request", { punchId });
            setReqMsg({ ok: true, text: res?.data?.message || "Deletion request submitted." });
            await openMissingReload();
        } catch (e) {
            const d = e?.response?.data;
            setReqMsg({ ok: false, text: (typeof d === "string" && d) || d?.message || "Request failed." });
        } finally {
            setReqBusy(false);
        }
    };

    // Go-live parity (2026-09-24), ATT#30: withdraw my own pending missing-punch request.
    const withdrawRequest = async (punchId) => {
        setReqBusy(true);
        setReqMsg(null);
        try {
            const res = await api.post(`WhosInBuilding/missing-punch-request/${punchId}/withdraw`);
            setReqMsg({ ok: true, text: res?.data?.message || "Request withdrawn." });
            await openMissingReload();
        } catch (e) {
            const d = e?.response?.data;
            setReqMsg({ ok: false, text: (typeof d === "string" && d) || d?.message || "Withdraw failed." });
        } finally {
            setReqBusy(false);
        }
    };

    // Reload without re-toggling the modal.
    const openMissingReload = async () => {
        try {
            const res = await api.get("WhosInBuilding/my-missing-punches?days=21");
            setMissingData(res.data || undefined);
        } catch { setMissingData(undefined); }
    };

    const openMissing = async () => {
        setMMissing(true);
        setMissingData(null);
        setReqMsg(null);
        setReqForm({ open: false, date: "", time: "", type: "Normal", inout: "IN" });
        try {
            const res = await api.get("WhosInBuilding/my-missing-punches?days=21");
            setMissingData(res.data || undefined);
        } catch {
            setMissingData(undefined); // controlled error state, no blank UI
        }
    };
    const [mAttend, setMAttend] = useState(false);
    const [mXlarge, setMXlarge] = useState(false);
    const [mRecords, setMRecords] = useState(false);

    /* ── clock state ── */
    const [clockInTime, setClockInTime] = useState(null);
    // End of the working day by company policy (8:00 – 4:30, lunch 12:00 – 12:30).
    // The server sends it from the same settings HR's Late-In / Early-Out reviews
    // use. It does not move with the clock-in time.
    const [expectedClockOut, setExpectedClockOut] = useState(null);
    const [status, setStatus] = useState('');
    const [maxTime, setMaxTime] = useState('');
    const [statusBreak, setStatusBreak] = useState('');
    const [maxTimeBreak, setMaxTimeBreak] = useState('');
    const [statusBusiness, setStatusBusiness] = useState('');
    const [maxTimeBusiness, setMaxTimeBusiness] = useState('');
    const [statusPersonal, setStatusPersonal] = useState('');
    const [maxTimePersonal, setMaxTimePersonal] = useState('');

    /* ── misc ── */
    const [whoAmI, setWhoAmI] = useState({ empId: "", imagePath: "" });
    // Owner (2026-10-01): the photo is the empPhotos row, fetched with the
    // sign-in by the shared hook; ImagePath is only the last fallback.
    const photo = useEmployeePhoto(whoAmI.empId, whoAmI.imagePath);
    const [attendanceData, setAttendanceData] = useState(null);
    const [activityFeed, setActivityFeed] = useState([]);
    const [liveClock, setLiveClock] = useState(new Date());

    /* ── punch records ── */
    const [punchRecords, setPunchRecords] = useState([]);
    const [loadingPunch, setLoadingPunch] = useState(false);
    const [punchFrom, setPunchFrom] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
    });
    const [punchTo, setPunchTo] = useState(() => new Date().toISOString().split("T")[0]);
    const [punchViewMode, setPunchViewMode] = useState("monthly");
    const [punchPage, setPunchPage] = useState(1);
    const ROWS = 7;

    /* ── live clock tick ── */
    useEffect(() => {
        const id = setInterval(() => setLiveClock(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    /* ── user image ── */
    useEffect(() => {
        const raw = sessionStorage.getItem("authUser");
        if (!raw) return;
        const obj = JSON.parse(raw);
        setWhoAmI({ empId: obj?.data?.uid || obj?.uid || "", imagePath: obj?.data?.imagePath || obj?.imagePath || "" });
    }, []);

    /* ── punches-today count (server-side; Round TA-1) ── */
    const [punchesToday, setPunchesToday] = useState(0);
    /* ── Round TA-6: grace-window undo of the most recent punch ── */
    const [undoInfo, setUndoInfo] = useState(null); // { lastTime, windowMin }
    const [undoBusy, setUndoBusy] = useState(false);
    const [undoMsg, setUndoMsg] = useState(null);

    const undoLastPunch = async () => {
        if (undoBusy) return;
        setUndoBusy(true);
        setUndoMsg(null);
        try {
            const res = await api.post("WhosInBuilding/undo-last-punch", {});
            setUndoMsg({ ok: true, text: res?.data?.message || "Punch undone." });
            if (empId) await initStatuses(empId);
            await loadToday();
        } catch (e) {
            const d = e?.response?.data;
            setUndoMsg({ ok: false, text: (typeof d === "string" && d) || d?.message || "Undo failed." });
        } finally {
            setUndoBusy(false);
        }
    };

    /* ── fetch last actions on mount ── */
    const authUser = sessionStorage.getItem("authUser");
    const empId = authUser ? JSON.parse(authUser)?.uid : null;

    useEffect(() => {
        if (empId) {
            initStatuses(empId);
            loadToday();
        }
    
    }, [empId]);

    const initStatuses = async (id) => {
        await Promise.allSettled([
            fetchAndSet(`WhosInBuilding/last-action/${id}`, "getLastAction", setMaxTime, setStatus),
            fetchAndSet(`WhosInBuilding/last-action-break/${id}`, "getLastActionBreak", setMaxTimeBreak, setStatusBreak),
            fetchAndSet(`WhosInBuilding/last-action-business/${id}`, "getLastActionBusiness", setMaxTimeBusiness, setStatusBusiness),
            fetchAndSet(`WhosInBuilding/last-action-Personal/${id}`, "getLastActionPersonal", setMaxTimePersonal, setStatusPersonal),
        ]);
    };

    /* ── today's real punch data (Round TA-1) ─────────────────────────
       GET /api/WhosInBuilding/my-status (JWT identity — no empId sent)
       feeds the activity log, the PUNCHES TODAY count and the CLOCK-IN
       TIME from actual TimeClock rows instead of a frontend-only feed
       and a sessionStorage timestamp. Called on mount and after every
       successful punch. */
    const FEED_LABELS = {
        "Normal|IN": { label: "Clocked in for the day", type: "in" },
        "Normal|OUT": { label: "Clocked out for the day", type: "out" },
        "Break|OUT": { label: "Out for lunch", type: "break" },
        "Break|IN": { label: "Back from lunch", type: "in" },
        "Business|OUT": { label: "Out for business", type: "break" },
        "Business|IN": { label: "Back from business", type: "in" },
        "Personal|OUT": { label: "Out for personal", type: "break" },
        "Personal|IN": { label: "Back from personal", type: "in" },
    };

    const loadToday = async () => {
        try {
            const res = await api.get("WhosInBuilding/my-status");
            const d = res.data || {};
            const punches = Array.isArray(d.todayPunches) ? d.todayPunches : [];

            setPunchesToday(
                typeof d.punchesToday === "number" ? d.punchesToday : punches.length
            );

            // Round TA-6: offer Undo while the newest punch is inside the
            // grace window (server enforces it again on click).
            const newest = punches.length ? punches[punches.length - 1] : null;
            setUndoInfo(newest ? {
                lastTime: newest.clockTime ?? newest.ClockTime,
                lastLabel: `${newest.status ?? newest.Status} ${new Date(newest.clockTime ?? newest.ClockTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
                windowMin: d.undoWindowMinutes || 10,
            } : null);

            // Newest first for the feed.
            const feed = [...punches].reverse().map((p) => {
                const key = `${p.clockType ?? p.ClockType}|${p.status ?? p.Status}`;
                const meta = FEED_LABELS[key] || { label: key.replace("|", " — "), type: "break" };
                const t = p.clockTime ?? p.ClockTime;
                return {
                    label: meta.label + ((p.manualSelect ?? p.ManualSelect) ? " (manual)" : (p.remote ?? p.Remote) ? " (remote)" : ""),
                    type: meta.type,
                    ts: t ? new Date(t).toLocaleTimeString() : "",
                };
            });
            setActivityFeed(feed);

            // Server-side first Normal IN of the day (or null).
            setExpectedClockOut(d.expectedClockOut || null);
            setClockInTime(d.clockInTime || null);
            if (d.clockInTime) {
                try { sessionStorage.setItem("clockInTime", d.clockInTime); } catch (e) { /* ignore */ }
            } else {
                try { sessionStorage.removeItem("clockInTime"); } catch (e) { /* ignore */ }
            }
        } catch { /* controlled: keep whatever is shown; no blank page */ }
    };

    // Round TA-1 fix: the previous implementation short-circuited to a
    // sessionStorage copy of the last-action response and never refetched,
    // so the page showed a stale IN/OUT status for the entire browser
    // session (e.g. "IN" with an empty activity log after a biometric punch
    // or a punch made on another device/tab). Current status must come from
    // server-side data — always fetch fresh; the sessionStorage copy is
    // still written for any legacy consumers but is never read here.
    const fetchAndSet = async (url, sessionKey, setTime, setStatusFn) => {
        try {
            const res = await api.get(url);
            if (res.data) {
                try { sessionStorage.setItem(sessionKey, JSON.stringify(res.data)); } catch (e) { /* ignore */ }
                setTime(res.data.maxTime?.replace("T", " ") ?? "");
                setStatusFn(res.data.status ?? "");
            }
        } catch { /* silent */ }
    };

    /* ── feed helper ── */
    const pushFeed = (label, type) => {
        const entry = { label, type, ts: new Date().toLocaleTimeString() };
        setActivityFeed(prev => [entry, ...prev].slice(0, FEED_MAX));
    };

    /* ─────────────────────────────
       Clock-in / Clock-out
    ───────────────────────────── */
    const handleClockIn = async () => {
        // Go-live parity (2026-09-24), ATT#28: after 9 PM the user picks the report day.
        setClockInType("tod");
        setMissedOutNote(null);
        try {
            const res = await api.get(`WhosInBuilding/last-action/${empId}`);
            // Go-live parity (2026-09-24), ATT#19: an IN left open from an EARLIER day
            // no longer blocks today's clock-in (legacy only warned).
            const lastDay = String(res.data?.maxTime || "").slice(0, 10);
            if (res.data.status === 'IN' && lastDay && lastDay < easternParts().date) {
                setMissedOutNote(`Your clock-out for ${lastDay} is missing. Clock in now, then submit a missing punch request for that day.`);
                setMClockIn(true);
                return;
            }
            res.data.status === 'IN' ? setMFirst(true) : setMClockIn(true);
        } catch { setMClockIn(true); }
    };

    const confirmClockIn = async () => {
        const ts = new Date().toISOString();
        try {
            const res = await api.post("WhosInBuilding/clock-in", { empId, myType: clockInType });
            if (res.status === 200 || res.status === 201) {
                setClockInTime(ts);
                sessionStorage.setItem("clockInTime", ts);
                fetchAndSet(`WhosInBuilding/last-action/${empId}`, "getLastAction", setMaxTime, setStatus);
                pushFeed("Clocked in for the day", "in");
                loadToday();
                setMClockIn(false);
                if (typeof res.data === "string" && res.data.includes("missing")) toast.warning(res.data);
            }
        } catch (e) {
            console.error(e);
            // Go-live parity (2026-09-24): show the server's reason (e.g. hourly staff).
            const d = e?.response?.data;
            toast.error((typeof d === "string" && d) || d?.message || "Clock in failed.");
        }
    };

    const handleClockOut = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action/${empId}`);
            res.data.status === 'OUT' ? setMSecond(true) : setMClockOut(true);
        } catch { setMClockOut(true); }
    };

    const confirmClockOut = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-out", { empId });
            if (res.status === 200 || res.status === 201) {
                setClockInTime(null);
                sessionStorage.removeItem("clockInTime");
                fetchAndSet(`WhosInBuilding/last-action/${empId}`, "getLastAction", setMaxTime, setStatus);
                pushFeed("Clocked out for the day", "out");
                loadToday();
                setMClockOut(false);
            }
        } catch (e) {
            console.error(e);
            // Go-live parity (2026-09-24), ATT#20: e.g. a task timer is still running.
            const d = e?.response?.data;
            toast.error((typeof d === "string" && d) || d?.message || "Clock out failed.");
        }
    };

    /* ─────────────────────────────
       Lunch handlers
    ───────────────────────────── */
    const handleLunchOut = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action/${empId}`);
            res.data.status === 'OUT' ? setMSecond(true) : setMLunchOut(true);
        } catch { setMLunchOut(true); }
    };
    const confirmLunchOut = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-out-for-lunch", { empId });
            if (res.status === 200 || res.status === 201) {
                fetchAndSet(`WhosInBuilding/last-action-break/${empId}`, "getLastActionBreak", setMaxTimeBreak, setStatusBreak);
                pushFeed("Out for lunch", "break");
                loadToday();
                setMLunchOut(false);
            }
        } catch (e) { console.error(e); }
    };
    const handleLunchIn = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action-break/${empId}`);
            res.data.status === 'IN' ? setMFirst(true) : setMLunchIn(true);
        } catch { setMLunchIn(true); }
    };
    const confirmLunchIn = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-in-for-lunch", { empId });
            if (res.status === 200 || res.status === 201) {
                fetchAndSet(`WhosInBuilding/last-action-break/${empId}`, "getLastActionBreak", setMaxTimeBreak, setStatusBreak);
                pushFeed("Back from lunch", "in");
                loadToday();
                setMLunchIn(false);
            }
        } catch (e) { console.error(e); }
    };

    /* ─────────────────────────────
       Business handlers
    ───────────────────────────── */
    const handleBizOut = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action/${empId}`);
            res.data.status === 'OUT' ? setMSecond(true) : setMBizOut(true);
        } catch { setMBizOut(true); }
    };
    const confirmBizOut = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-out-for-business", { empId });
            if (res.status === 200 || res.status === 201) {
                fetchAndSet(`WhosInBuilding/last-action-business/${empId}`, "getLastActionBusiness", setMaxTimeBusiness, setStatusBusiness);
                pushFeed("Out for business", "break");
                loadToday();
                setMBizOut(false);
            }
        } catch (e) { console.error(e); }
    };
    const handleBizIn = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action-business/${empId}`);
            res.data.status === 'IN' ? setMFirst(true) : setMBizIn(true);
        } catch { setMBizIn(true); }
    };
    const confirmBizIn = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-in-for-business", { empId });
            if (res.status === 200 || res.status === 201) {
                fetchAndSet(`WhosInBuilding/last-action-business/${empId}`, "getLastActionBusiness", setMaxTimeBusiness, setStatusBusiness);
                pushFeed("Back from business", "in");
                loadToday();
                setMBizIn(false);
            }
        } catch (e) { console.error(e); }
    };

    /* ─────────────────────────────
       Personal handlers
    ───────────────────────────── */
    const handlePerOut = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action/${empId}`);
            res.data.status === 'OUT' ? setMSecond(true) : setMPerOut(true);
        } catch { setMPerOut(true); }
    };
    const confirmPerOut = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-out-for-personal", { empId });
            if (res.status === 200 || res.status === 201) {
                fetchAndSet(`WhosInBuilding/last-action-personal/${empId}`, "getLastActionPersonal", setMaxTimePersonal, setStatusPersonal);
                pushFeed("Out for personal", "break");
                loadToday();
                setMPerOut(false);
            }
        } catch (e) { console.error(e); }
    };
    const handlePerIn = async () => {
        try {
            const res = await api.get(`WhosInBuilding/last-action-personal/${empId}`);
            res.data.status === 'IN' ? setMFirst(true) : setMPerIn(true);
        } catch { setMPerIn(true); }
    };
    const confirmPerIn = async () => {
        try {
            const res = await api.post("WhosInBuilding/clock-in-for-personal", { empId });
            if (res.status === 200 || res.status === 201) {
                fetchAndSet(`WhosInBuilding/last-action-personal/${empId}`, "getLastActionPersonal", setMaxTimePersonal, setStatusPersonal);
                pushFeed("Back from personal", "in");
                loadToday();
                setMPerIn(false);
            }
        } catch (e) { console.error(e); }
    };

    /* ─────────────────────────────
       Attendance view
    ───────────────────────────── */
    const handleAttendanceView = async () => {
        try {
            const res = await api.get(`WhosInBuilding/work-time/${empId}`);
            if (res.status === 200 || res.status === 201) {
                if (Array.isArray(res.data) && res.data.length === 0) {
                    toast.error("Clock-In is necessary");
                } else {
                    setAttendanceData(res.data);
                    setMAttend(true);
                }
            }
        } catch (e) { console.error(e); }
    };

    /* ─────────────────────────────
       Punch records
    ───────────────────────────── */
    const handlePunchRecords = () => navigate("/time-and-attendance/punch-record");
    const handleWhosInBuilding = () => navigate("/time-and-attendance/whos-in-building");

    /* ─────────────────────────────
       Helpers / derived values
    ───────────────────────────── */
    const fmtClock = (d) =>
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const todayLabel = `${days[liveClock.getDay()]}, ${months[liveClock.getMonth()]} ${liveClock.getDate()}, ${liveClock.getFullYear()}`;

    /* ════════════════════════════
       RENDER
    ════════════════════════════ */
    return (
        <React.Fragment>
            <ToastContainer />

            {/* ── global page styles ── */}
            <style>{pageCSS}</style>

            <div className="page-content ta-page">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-fingerprint-line"
                        eyebrow="Attendance"
                        title="Time and Attendance"
                        subtitle="Punch records, building presence, and biometric tools."
                        tone="blue"
                        breadcrumb={[
                            { label: "Dashboards", to: "/" },
                            { label: "Time and Attendance" },
                        ]}
                    />

                    {/* ═══════════════════════════════════════
                        TOP GRID  –  Profile card | Right panel
                    ═══════════════════════════════════════ */}
                    <div className="ta-top-grid">

                        {/* ── LEFT: profile + timer + break mini-stats ── */}
                        <div className="ta-profile-card">

                            {/* avatar + name */}
                            <div className="ta-avatar-row">
                                <div className="ta-avatar-wrap">
                                    <img src={photo.src} onError={photo.onError} alt="user" className="ta-avatar-img" />
                                    <span className={`ta-status-dot ${status === 'IN' ? 'dot-in' : 'dot-out'}`} />
                                </div>
                                <div>
                                    <div className="ta-name">My Attendance</div>
                                    <div className="ta-sub">
                                        <span className={`ta-badge ${status === 'IN' ? 'badge-in' : status === 'OUT' ? 'badge-out' : 'badge-na'}`}>
                                            {status || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="ta-maxtime">{maxTime || '—'}</div>
                                </div>
                            </div>

                            {/* live clock */}
                            <div className="ta-live-clock-box">
                                <div className="ta-live-time">{fmtClock(liveClock)}</div>
                                <div className="ta-live-date">{todayLabel}</div>
                            </div>

                            {/* working-hours timer */}
                            <div className="ta-timer-box">
                                <div className="ta-timer-label">
                                    <FiClock size={13} style={{ marginRight: 5 }} />Working hours
                                </div>
                                {clockInTime && status === 'IN' ? (
                                    <div className="ta-timer-val">
                                        <Timer clockInTime={clockInTime} />
                                    </div>
                                ) : clockInTime ? (
                                    <div className="ta-timer-empty">Clocked out</div>
                                ) : (
                                    <div className="ta-timer-empty">Clock-in required</div>
                                )}
                            </div>

                            {/* Round TA-6: grace-window undo of the most recent punch.
                                Fixes the "clocked out by mistake" case the physical
                                clock's 3-minute guard can't distinguish. */}
                            {undoInfo && (Date.now() - new Date(undoInfo.lastTime).getTime()) < undoInfo.windowMin * 60000 && (
                                <div className="text-center mt-2">
                                    <Button size="sm" color="outline-warning" disabled={undoBusy} onClick={undoLastPunch}>
                                        {undoBusy ? <span className="spinner-border spinner-border-sm" /> : `Undo last punch (${undoInfo.lastLabel})`}
                                    </Button>
                                </div>
                            )}
                            {undoMsg && (
                                <div className={`text-center mt-1 fs-12 ${undoMsg.ok ? "text-success" : "text-danger"}`}>{undoMsg.text}</div>
                            )}

                            {/* break mini stats */}
                            <div className="ta-mini-grid">
                                {[
                                    { label: 'Lunch', status: statusBreak, time: maxTimeBreak },
                                    { label: 'Business', status: statusBusiness, time: maxTimeBusiness },
                                    { label: 'Personal', status: statusPersonal, time: maxTimePersonal },
                                ].map(({ label, status: s, time: t }) => (
                                    <div key={label} className="ta-mini-stat">
                                        <div className="ta-mini-label">{label}</div>
                                        <div className={`ta-mini-val ${s === 'IN' ? 'val-in' : s === 'OUT' ? 'val-out' : 'val-na'}`}>{s || 'N/A'}</div>
                                        <div className="ta-mini-time">{t || '—'}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── RIGHT: actions + tools + feed ── */}
                        <div className="ta-right-panel">

                            {/* ── ACTION GROUPS ── */}
                            <div className="ta-action-card">
                                {[
                                    {
                                        icon: <FiLogIn size={14} />, title: 'In / Out',
                                        buttons: [
                                            { label: 'In for Day', color: 'primary', onClick: handleClockIn },
                                            { label: 'Out for Day', color: 'danger', onClick: handleClockOut },
                                        ]
                                    },
                                    {
                                        icon: <FiCoffee size={14} />, title: 'Lunch',
                                        buttons: [
                                            { label: 'Out Lunch', color: 'info', onClick: handleLunchOut },
                                            { label: 'Back Lunch', color: 'success', onClick: handleLunchIn },
                                        ]
                                    },
                                    {
                                        icon: <FiBriefcase size={14} />, title: 'Business',
                                        buttons: [
                                            { label: 'Out Biz', color: 'warning', onClick: handleBizOut },
                                            { label: 'Back Biz', color: 'success', onClick: handleBizIn },
                                        ]
                                    },
                                    {
                                        icon: <FiUserCheck size={14} />, title: 'Personal',
                                        buttons: [
                                            { label: 'Out Personal', color: 'secondary', onClick: handlePerOut },
                                            { label: 'Back Personal', color: 'primary', onClick: handlePerIn },
                                        ]
                                    },
                                ].map(({ icon, title, buttons }) => (
                                    <div key={title} className="ta-action-group">
                                        <div className="ta-group-header">{icon}<span>{title}</span></div>
                                        <div className="ta-group-btns">
                                            {buttons.map(({ label, color, onClick }) => (
                                                <button
                                                    key={label}
                                                    className={`ta-abtn ta-abtn-${color}`}
                                                    onClick={onClick}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── QUICK TOOLS ── */}
                            <div className="ta-tools-card">
                                <div className="ta-tools-title">Quick Tools</div>
                                <div className="ta-tools-row">
                                    {[
                                        { icon: <FiUserX size={15} />, label: 'Missing Punches', onClick: openMissing },
                                        { icon: <FiClock size={15} />, label: 'Punch Records', onClick: handlePunchRecords },
                                        { icon: <FiActivity size={15} />, label: "Who's In Building?", onClick: handleWhosInBuilding },
                                        { icon: <FiLogIn size={15} />, label: 'View Attendance', onClick: handleAttendanceView },
                                    ].map(({ icon, label, onClick }) => (
                                        <button key={label} className="ta-tool-btn" onClick={onClick}>
                                            <span className="ta-tool-icon">{icon}</span>
                                            <span>{label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* ── ACTIVITY FEED ── */}
                            <div className="ta-feed-card">
                                <div className="ta-feed-header">
                                    <FiClock size={14} style={{ marginRight: 6 }} />Today's Activity Log
                                </div>
                                <div className="ta-feed-list">
                                    {activityFeed.length === 0 ? (
                                        <div className="ta-feed-empty">No activity yet — clock in to get started.</div>
                                    ) : activityFeed.map((item, i) => (
                                        <div key={i} className="ta-feed-item">
                                            <span className={`ta-feed-dot dot-${item.type}`} />
                                            <span className="ta-feed-text">{item.label}</span>
                                            <span className={`ta-feed-badge badge-${item.type}`}>
                                                {item.type === 'in' ? 'IN' : item.type === 'out' ? 'OUT' : 'BREAK'}
                                            </span>
                                            <span className="ta-feed-time">{item.ts}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* ═══════════════════════════════════════
                        BOTTOM STATS ROW
                    ═══════════════════════════════════════ */}
                    <div className="ta-stats-row">
                        {[
                            { label: 'Punches Today', value: punchesToday, color: '#534AB7' }, // Round TA-1: real server count
                            { label: 'Clock-In Time', value: clockInTime ? new Date(clockInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—', mono: true },
                            { label: 'Expected Clock-Out', value: expectedClockOutText(clockInTime, expectedClockOut), mono: true },
                            { label: 'Current Status', value: status || 'N/A', pill: true, pillClass: status === 'IN' ? 'badge-in' : status === 'OUT' ? 'badge-out' : 'badge-na' },
                        ].map(({ label, value, color, mono, pill, pillClass }) => (
                            <div key={label} className="ta-stat-card">
                                <div className="ta-stat-label">{label}</div>
                                {pill
                                    ? <span className={`ta-badge ta-stat-badge ${pillClass}`}>{value}</span>
                                    : <div className="ta-stat-value" style={{ color: color ? inkFor(color) : 'inherit', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
                                }
                            </div>
                        ))}
                    </div>

                </Container>
            </div>

            {/* ══════════════════════════════════
                MODALS
            ══════════════════════════════════ */}

            {/* Already IN */}
            <Modal isOpen={mFirst} toggle={() => setMFirst(false)} centered>
                <ModalHeader toggle={() => setMFirst(false)}>Already Clocked In</ModalHeader>
                <ModalBody className="text-center p-5">
                    <lord-icon src="https://cdn.lordicon.com/tdrtiskw.json" trigger="loop" colors="primary:#f7b84b,secondary:#405189" style={{ width: 120, height: 120 }} />
                    <h5 className="mt-4">You're already clocked in!</h5>
                    <p className="text-muted">Please clock out before clocking in again.</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="warning" onClick={() => setMFirst(false)}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Already OUT */}
            <Modal isOpen={mSecond} toggle={() => setMSecond(false)} centered>
                <ModalHeader toggle={() => setMSecond(false)}>Already Clocked Out</ModalHeader>
                <ModalBody className="text-center p-5">
                    <lord-icon src="https://cdn.lordicon.com/tdrtiskw.json" trigger="loop" colors="primary:#405189,secondary:#0ab39c" style={{ width: 120, height: 120 }} />
                    <h5 className="mt-4">You're already clocked out!</h5>
                    <p className="text-muted">Please clock in first.</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="warning" onClick={() => setMSecond(false)}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Clock In */}
            <ConfirmModal isOpen={mClockIn} toggle={() => setMClockIn(false)} title="Clock In for the Day" onConfirm={confirmClockIn} confirmLabel="Clock In">
                {/* Go-live parity (2026-09-24), ATT#19 / ATT#28 */}
                {missedOutNote && (
                    <div className="alert alert-warning mt-3 mb-0 py-2 fs-12 text-start">{missedOutNote}</div>
                )}
                {mClockIn && easternParts().hour >= 21 && (
                    <div className="mt-3 text-start">
                        <div className="fs-12 text-muted mb-1">It is after 9 PM — which work day is this clock-in for?</div>
                        <div className="d-flex gap-3">
                            <label className="fs-13"><input type="radio" name="clockInType" className="me-1"
                                checked={clockInType === "tod"} onChange={() => setClockInType("tod")} />Today</label>
                            <label className="fs-13"><input type="radio" name="clockInType" className="me-1"
                                checked={clockInType === "tom"} onChange={() => setClockInType("tom")} />Tomorrow</label>
                        </div>
                    </div>
                )}
            </ConfirmModal>
            {/* Clock Out */}
            <ConfirmModal isOpen={mClockOut} toggle={() => setMClockOut(false)} title="Clock Out for the Day" onConfirm={confirmClockOut} confirmLabel="Clock Out" confirmColor="danger" />
            {/* Lunch Out */}
            <ConfirmModal isOpen={mLunchOut} toggle={() => setMLunchOut(false)} title="Clock Out for Lunch" onConfirm={confirmLunchOut} confirmLabel="Out for Lunch" confirmColor="info" />
            {/* Lunch In */}
            <ConfirmModal isOpen={mLunchIn} toggle={() => setMLunchIn(false)} title="Back In from Lunch" onConfirm={confirmLunchIn} confirmLabel="Back from Lunch" confirmColor="success" />
            {/* Biz Out */}
            <ConfirmModal isOpen={mBizOut} toggle={() => setMBizOut(false)} title="Clock Out for Business" onConfirm={confirmBizOut} confirmLabel="Out for Business" confirmColor="warning" />
            {/* Biz In */}
            <ConfirmModal isOpen={mBizIn} toggle={() => setMBizIn(false)} title="Back In from Business" onConfirm={confirmBizIn} confirmLabel="Back from Business" confirmColor="success" />
            {/* Per Out */}
            <ConfirmModal isOpen={mPerOut} toggle={() => setMPerOut(false)} title="Clock Out for Personal" onConfirm={confirmPerOut} confirmLabel="Out for Personal" />
            {/* Per In */}
            <ConfirmModal isOpen={mPerIn} toggle={() => setMPerIn(false)} title="Back In from Personal" onConfirm={confirmPerIn} confirmLabel="Back from Personal" confirmColor="primary" />

            {/* Missing Punches */}
            <Modal isOpen={mMissing} toggle={() => setMMissing(false)} backdrop="static" centered size="lg">
                <ModalHeader toggle={() => setMMissing(false)}>
                    My Missing Punches <span className="text-muted fw-normal fs-13">(last 21 days)</span>
                </ModalHeader>
                <ModalBody className={!missingData || !missingData.count ? "text-center p-5" : "p-3"}>
                    {missingData === null ? (
                        <div className="py-4">
                            <span className="spinner-border text-primary" role="status" />
                            <p className="text-muted mt-3 mb-0">Checking your punches…</p>
                        </div>
                    ) : missingData === undefined ? (
                        <div className="py-4">
                            <i className="ri-error-warning-line text-danger" style={{ fontSize: 42 }} />
                            <h5 className="mt-3 mb-1">Couldn't load missing punches</h5>
                            <p className="text-muted mb-0">Please try again or contact support.</p>
                        </div>
                    ) : missingData.count === 0 ? (
                        <>
                            <lord-icon src="https://cdn.lordicon.com/lupuorrc.json" trigger="loop" colors="primary:#121331,secondary:#08a88a" style={{ width: 120, height: 120 }} />
                            <h5 className="mt-4 mb-2">No Missing Punches</h5>
                            <p className="text-muted">No missing punches detected 🎉</p>
                            {reqMsg && <div className={`alert py-2 mt-2 ${reqMsg.ok ? "alert-success" : "alert-danger"}`}>{reqMsg.text}</div>}
                        </>
                    ) : (
                        <>
                            <p className="text-muted mb-2">
                                {missingData.count} day{missingData.count > 1 ? "s" : ""} with unbalanced or pending punches.
                                Submit a correction request below — your supervisor / HR will review it.
                            </p>
                            {reqMsg && <div className={`alert py-2 ${reqMsg.ok ? "alert-success" : "alert-danger"}`}>{reqMsg.text}</div>}
                            {/* Round TA-3: request-a-missing-punch form (legacy submit parity) */}
                            {!reqForm.open ? (
                                <Button size="sm" color="primary" className="mb-2" onClick={() => setReqForm((f) => ({ ...f, open: true }))}>
                                    <i className="ri-add-line me-1" />Request a missing punch
                                </Button>
                            ) : (
                                <div className="border rounded p-2 mb-2 bg-light">
                                    <div className="d-flex flex-wrap gap-2 align-items-end">
                                        <div>
                                            <label className="form-label fs-11 mb-0">Working day</label>
                                            <input type="date" className="form-control form-control-sm"
                                                max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                                                value={reqForm.date}
                                                onChange={(e) => setReqForm((f) => ({ ...f, date: e.target.value }))} />
                                        </div>
                                        <div>
                                            <label className="form-label fs-11 mb-0">Time</label>
                                            <input type="time" className="form-control form-control-sm"
                                                value={reqForm.time}
                                                onChange={(e) => setReqForm((f) => ({ ...f, time: e.target.value }))} />
                                        </div>
                                        <div>
                                            <label className="form-label fs-11 mb-0">Type</label>
                                            <select className="form-select form-select-sm" value={reqForm.type}
                                                onChange={(e) => setReqForm((f) => ({ ...f, type: e.target.value }))}>
                                                <option>Normal</option><option>Break</option>
                                                <option>Business</option><option>Personal</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="form-label fs-11 mb-0">IN/OUT</label>
                                            <select className="form-select form-select-sm" value={reqForm.inout}
                                                onChange={(e) => setReqForm((f) => ({ ...f, inout: e.target.value }))}>
                                                <option>IN</option><option>OUT</option>
                                            </select>
                                        </div>
                                        <div className="d-flex gap-1">
                                            <Button size="sm" color="success" disabled={reqBusy} onClick={submitMissingRequest}>
                                                {reqBusy ? <span className="spinner-border spinner-border-sm" /> : "Submit"}
                                            </Button>
                                            <Button size="sm" color="light" onClick={() => setReqForm((f) => ({ ...f, open: false }))}>Cancel</Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div style={{ maxHeight: 420, overflowY: "auto" }}>
                                {missingData.missingDays.map((d) => (
                                    <div key={d.reportDate} className="border rounded p-2 mb-2">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <strong>{new Date(d.reportDate).toLocaleDateString([], { weekday: "short", year: "numeric", month: "short", day: "numeric" })}</strong>
                                            <span className="text-danger fs-12">{d.reasons.join(" · ")}</span>
                                        </div>
                                        <table className="table table-sm mb-0 fs-12">
                                            <thead>
                                                <tr><th>Time</th><th>Type</th><th>IN/OUT</th><th>Approval</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {d.punches.map((pch) => (
                                                    <tr key={pch.id}>
                                                        <td>{new Date(pch.clockTime).toLocaleTimeString()}</td>
                                                        <td>{pch.clockType}{pch.manualSelect ? " (manual)" : pch.remote ? " (remote)" : ""}</td>
                                                        <td>
                                                            <span className={`badge ${String(pch.status).trim().toUpperCase() === "IN" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
                                                                {pch.status}
                                                            </span>
                                                        </td>
                                                        <td className={pch.approval && pch.approval.toLowerCase().includes("pending") ? "text-warning" : "text-muted"}>
                                                            {pch.approval || "—"}
                                                        </td>
                                                        <td>
                                                            {(!pch.approval || !pch.approval.toLowerCase().includes("pending")) && (
                                                                <Button size="sm" color="link" className="p-0 text-danger fs-11"
                                                                    disabled={reqBusy}
                                                                    title="Ask HR to delete this wrong punch"
                                                                    onClick={() => requestDeletion(pch.id)}>
                                                                    request deletion
                                                                </Button>
                                                            )}
                                                            {/* Go-live parity (2026-09-24), ATT#30 */}
                                                            {String(pch.approval || "").trim().toUpperCase() === "PENDING APPROVAL" && (
                                                                <Button size="sm" color="link" className="p-0 text-warning fs-11"
                                                                    disabled={reqBusy}
                                                                    title="Withdraw this missing-punch request"
                                                                    onClick={() => withdrawRequest(pch.id)}>
                                                                    withdraw
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Link to="#" className="btn btn-link link-success fw-medium" onClick={() => setMMissing(false)}>
                        <i className="ri-close-line me-1" /> Close
                    </Link>
                </ModalFooter>
            </Modal>

            {/* View Attendance */}
            <Modal size="xl" isOpen={mAttend} toggle={() => setMAttend(false)}>
                <ModalHeader toggle={() => setMAttend(false)}>
                    <FeatherIcon icon="clock" className="text-secondary me-2 icon-sm" /> View Attendance
                </ModalHeader>
                <ModalBody>
                    {attendanceData ? (
                        <Table bordered>
                            <thead>
                                <tr><th>Timestamp</th><th>Status</th><th>Time</th></tr>
                            </thead>
                            <tbody>
                                <tr><td>Clock In</td><td>Clocked In</td><td>{new Date(attendanceData.clockInTime).toLocaleString()}</td></tr>
                                <tr><td>Clock Out</td><td>Clocked Out</td><td>{attendanceData.clockOutTime ? new Date(attendanceData.clockOutTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td colSpan={3} style={{ padding: 1 }} /></tr>
                                <tr><td>Break Start</td><td>{attendanceData.breakStartTime ? 'On Break' : 'No Break'}</td><td>{attendanceData.breakStartTime ? new Date(attendanceData.breakStartTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td>Break End</td><td>{attendanceData.breakEndTime ? 'Break Ended' : 'No Break'}</td><td>{attendanceData.breakEndTime ? new Date(attendanceData.breakEndTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td><b>Total Break</b></td><td colSpan={2} className="text-center bg-light"><b>{attendanceData.breakDuration?.toFixed(2) ?? 'N/A'} min</b></td></tr>
                                <tr><td colSpan={3} style={{ padding: 1 }} /></tr>
                                <tr><td>Business Start</td><td>{attendanceData.businessStartTime ? 'On Business' : 'No Business'}</td><td>{attendanceData.businessStartTime ? new Date(attendanceData.businessStartTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td>Business End</td><td>{attendanceData.businessEndTime ? 'Business Ended' : 'No Business'}</td><td>{attendanceData.businessEndTime ? new Date(attendanceData.businessEndTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td><b>Total Business</b></td><td colSpan={2} className="text-center bg-light"><b>{attendanceData.businessDuration?.toFixed(2) ?? 'N/A'} min</b></td></tr>
                                <tr><td colSpan={3} style={{ padding: 1 }} /></tr>
                                <tr><td>Personal Start</td><td>{attendanceData.personalStartTime ? 'On Personal' : 'No Personal'}</td><td>{attendanceData.personalStartTime ? new Date(attendanceData.personalStartTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td>Personal End</td><td>{attendanceData.personalEndTime ? 'Personal Ended' : 'No Personal'}</td><td>{attendanceData.personalEndTime ? new Date(attendanceData.personalEndTime).toLocaleString() : 'N/A'}</td></tr>
                                <tr><td><b>Total Personal</b></td><td colSpan={2} className="text-center bg-light"><b>{attendanceData.personalDuration?.toFixed(2) ?? 'N/A'} min</b></td></tr>
                                <tr><td colSpan={3} style={{ padding: 1 }} /></tr>
                                <tr className="table-success"><td><b>Total Worked Time</b></td><td colSpan={2} className="text-center"><b>{attendanceData.totalWorkedTime}</b></td></tr>
                            </tbody>
                        </Table>
                    ) : <p>Loading attendance data...</p>}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setMAttend(false)}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Who's In Building */}
            <Modal size="xl" isOpen={mXlarge} toggle={() => setMXlarge(false)}>
                <ModalHeader toggle={() => setMXlarge(false)}>Who's In the Building?</ModalHeader>
                <ModalBody><WhoisInBuilding /></ModalBody>
                <ModalFooter>
                    <Link to="#" className="btn btn-link link-success fw-medium" onClick={() => setMXlarge(false)}>
                        <i className="ri-close-line me-1" /> Close
                    </Link>
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

/* ═══════════════════════════════════════════════════
   PAGE CSS  –  all scoped under .ta-page
═══════════════════════════════════════════════════ */
const pageCSS = `
/* ── layout ── */
.ta-page { padding-bottom: calc(60px + 1rem); } /* room for the 60px footer + a little air (was 2rem: footer covered the last cards) */

.ta-top-grid {
  display: grid;
  grid-template-columns: 288px 1fr;
  gap: 14px;
  margin-bottom: 14px;
  align-items: start;
}

@media (max-width: 900px) {
  .ta-top-grid { grid-template-columns: 1fr; }
}

/* ── profile card ── */
.ta-profile-card {
  background: var(--ssw-surface, #fff);
  border: 0.5px solid var(--ssw-line, #e4e4e7);
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ta-avatar-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ta-avatar-wrap { position: relative; display: inline-block; }

.ta-avatar-img {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--ssw-line, #EEEDFE);
}

.ta-status-dot {
  position: absolute;
  bottom: 2px; right: 2px;
  width: 12px; height: 12px;
  border-radius: 50%;
  border: 2px solid var(--ssw-surface, #fff);
}
.dot-in  { background: #22c55e; }
.dot-out { background: #ef4444; }

.ta-name { font-size: 14px; font-weight: 600; color: var(--ssw-fg-1, #111); }
.ta-maxtime { font-size: 11px; color: var(--ssw-fg-2, #888); font-family: monospace; margin-top: 3px; }

/* badges */
.ta-badge { display: inline-block; padding: 2px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.badge-in  { background: var(--ssw-tint-green, #dcfce7); color: var(--ssw-ink-green, #15803d); }
.badge-out { background: var(--ssw-tint-red, #fee2e2); color: var(--ssw-ink-red, #b91c1c); }
.badge-na  { background: var(--ssw-surface-3, #f1f5f9); color: var(--ssw-fg-2, #64748b); }

/* live clock */
.ta-live-clock-box {
  background: var(--ssw-surface-3, #f8f7ff);
  border-radius: 10px;
  padding: 12px 14px;
  text-align: center;
}
.ta-live-time {
  font-size: 26px;
  font-weight: 600;
  font-family: 'DM Mono', 'Courier New', monospace;
  color: var(--ssw-ink-indigo, #534AB7);
  letter-spacing: 2px;
}
.ta-live-date { font-size: 11px; color: var(--ssw-fg-2, #888); margin-top: 3px; }

/* timer box */
.ta-timer-box {
  background: var(--ssw-surface-3, #f8fafc);
  border: 0.5px solid var(--ssw-line, #e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
  text-align: center;
}
.ta-timer-label { font-size: 11px; color: var(--ssw-fg-2, #888); margin-bottom: 6px; display: flex; align-items: center; justify-content: center; }
.ta-timer-val {
  font-size: 22px;
  font-weight: 600;
  font-family: 'DM Mono', 'Courier New', monospace;
  color: var(--ssw-fg-1, #222);
  letter-spacing: 1px;
}
.ta-timer-empty { font-size: 13px; color: var(--ssw-fg-3, #aaa); }

/* mini stats */
.ta-mini-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }

.ta-mini-stat {
  background: var(--ssw-surface-3, #f8fafc);
  border: 0.5px solid var(--ssw-line, #e2e8f0);
  border-radius: 8px;
  padding: 8px 6px;
  text-align: center;
}
.ta-mini-label { font-size: 10px; color: var(--ssw-fg-2, #888); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.ta-mini-val { font-size: 13px; font-weight: 600; }
.ta-mini-time { font-size: 10px; color: var(--ssw-fg-3, #aaa); margin-top: 3px; font-family: monospace; }
.val-in  { color: var(--ssw-ink-green, #15803d); }
.val-out { color: var(--ssw-ink-red, #b91c1c); }
.val-na  { color: var(--ssw-fg-3, #94a3b8); }

/* ── right panel ── */
.ta-right-panel { display: flex; flex-direction: column; gap: 12px; }

/* action card */
.ta-action-card {
  background: var(--ssw-surface, #fff);
  border: 0.5px solid var(--ssw-line, #e4e4e7);
  border-radius: 14px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
}

.ta-action-group {
  padding: 0 12px;
  border-right: 0.5px solid var(--ssw-line, #e4e4e7);
}
.ta-action-group:first-child { padding-left: 0; }
.ta-action-group:last-child  { border-right: none; padding-right: 0; }

.ta-group-header {
  display: flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; color: var(--ssw-fg-1, #555);
  margin-bottom: 9px;
  text-transform: uppercase; letter-spacing: .4px;
}

.ta-group-btns { display: flex; flex-direction: column; gap: 6px; }

.ta-abtn {
  width: 100%;
  padding: 7px 8px;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: filter .12s, transform .1s;
  letter-spacing: .2px;
}
.ta-abtn:hover  { filter: brightness(.92); }
.ta-abtn:active { transform: scale(.97); }

.ta-abtn-primary   { background: var(--ssw-tint-indigo, #EEEDFE); color: var(--ssw-ink-indigo, #3C3489); }
.ta-abtn-danger    { background: var(--ssw-tint-red, #fee2e2); color: var(--ssw-ink-red, #b91c1c); }
.ta-abtn-info      { background: var(--ssw-tint-blue, #dbeafe); color: var(--ssw-ink-blue, #1d4ed8); }
.ta-abtn-success   { background: var(--ssw-tint-green, #dcfce7); color: var(--ssw-ink-green, #15803d); }
.ta-abtn-warning   { background: var(--ssw-tint-yellow, #fef3c7); color: var(--ssw-ink-orange, #92400e); }
.ta-abtn-secondary { background: var(--ssw-surface-3, #f1f5f9); color: var(--ssw-fg-1, #334155); }

@media (max-width: 700px) {
  .ta-action-card { grid-template-columns: repeat(2, 1fr); }
  .ta-action-group { border-right: none; border-bottom: 0.5px solid var(--ssw-line, #e4e4e7); padding: 10px 0; }
  .ta-action-group:last-child { border-bottom: none; }
}

/* tools card */
.ta-tools-card {
  background: var(--ssw-surface, #fff);
  border: 0.5px solid var(--ssw-line, #e4e4e7);
  border-radius: 14px;
  padding: 14px 16px;
}
.ta-tools-title { font-size: 11px; font-weight: 600; color: var(--ssw-fg-2, #888); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 10px; }
.ta-tools-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }

.ta-tool-btn {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 10px;
  border-radius: 8px;
  border: 0.5px solid var(--ssw-line, #e4e4e7);
  background: var(--ssw-surface, #fff);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--ssw-fg-1, #333);
  transition: background .12s, border-color .12s;
  white-space: nowrap;
}
.ta-tool-btn:hover { background: var(--ssw-surface-3, #f8f7ff); border-color: #c4b9f8; }
.ta-tool-icon { color: var(--ssw-ink-indigo, #534AB7); flex-shrink: 0; display: flex; }

@media (max-width: 700px) {
  .ta-tools-row { grid-template-columns: repeat(2, 1fr); }
}

/* feed card */
.ta-feed-card {
  background: var(--ssw-surface, #fff);
  border: 0.5px solid var(--ssw-line, #e4e4e7);
  border-radius: 14px;
  padding: 14px 16px;
  flex: 1;
  min-height: 120px;
}
.ta-feed-header { font-size: 11px; font-weight: 600; color: var(--ssw-fg-2, #888); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 10px; display: flex; align-items: center; }
.ta-feed-list { display: flex; flex-direction: column; }
.ta-feed-empty { font-size: 12px; color: var(--ssw-fg-3, #bbb); font-style: italic; padding: 6px 0; }

.ta-feed-item {
  display: flex; align-items: center; gap: 9px;
  padding: 7px 0;
  border-bottom: 0.5px solid var(--ssw-line, #f1f5f9);
  font-size: 12px;
}
.ta-feed-item:last-child { border-bottom: none; }

.ta-feed-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot-in    { background: #22c55e; }
.dot-out   { background: #ef4444; }
.dot-break { background: #3b82f6; }

.ta-feed-text { flex: 1; color: var(--ssw-fg-1, #333); }
.ta-feed-time { font-size: 11px; color: var(--ssw-fg-3, #aaa); font-family: monospace; }

.ta-feed-badge { font-size: 10px; padding: 2px 7px; border-radius: 20px; font-weight: 600; }
.badge-in    { background: var(--ssw-tint-green, #dcfce7); color: var(--ssw-ink-green, #15803d); }
.badge-out   { background: var(--ssw-tint-red, #fee2e2); color: var(--ssw-ink-red, #b91c1c); }
.badge-break { background: var(--ssw-tint-blue, #dbeafe); color: var(--ssw-ink-blue, #1d4ed8); }

/* ── bottom stats ── */
.ta-stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

.ta-stat-card {
  background: var(--ssw-surface, #fff);
  border: 0.5px solid var(--ssw-line, #e4e4e7);
  border-radius: 12px;
  padding: 14px 16px;
  text-align: center;
}
.ta-stat-label { font-size: 11px; color: var(--ssw-fg-2, #888); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 8px; }
.ta-stat-value { font-size: 22px; font-weight: 600; }
.ta-stat-badge { font-size: 14px; padding: 4px 14px; }

@media (max-width: 700px) {
  .ta-stats-row { grid-template-columns: repeat(2, 1fr); }
}
`;

export default DashboardCrypto;